import React from 'react';
import { useStateContext } from '../contexts/ContextProvider';
import { formatDate, truncate } from '../data/utils';
import {
	Category,
	ChartComponent,
	ColumnSeries,
	DataLabel,
	Inject,
	Legend,
	SeriesCollectionDirective,
	SeriesDirective,
	PieSeries,
	Tooltip,
	AccumulationChartComponent,
	AccumulationSeriesCollectionDirective,
	AccumulationSeriesDirective,
	AccumulationLegend,
	AccumulationDataLabel,
	AccumulationTooltip,
} from '@syncfusion/ej2-react-charts';
import { AiOutlineAlert, AiOutlineArrowDown, AiOutlineCheckCircle, AiOutlineExclamationCircle, AiOutlineEye, AiOutlineLock, AiOutlineUnlock } from 'react-icons/ai';
import Header from '../components/Header';
import { mockAnalytics } from '../data/mockData';
import { useFetch } from '../hooks/useFetch';
import LoadingOverlay from '../components/LoadingOverlay';
import { getStatusBadge } from './Alerts';


const Dashboard = () => {
	const { currentColor, currentMode, getCategory } = useStateContext();
	const { loading, data: alertsData } = useFetch('/dashboard/analytics', null, mockAnalytics);

	const primaryXAxis = { valueType: 'Category', title: 'Mois' };
	const primaryYAxis = { title: 'Nombre d\'alertes' };

	const pieChartData = alertsData?.alertsByStatus?.map(item => ({
		x: item.status,
		y: item.count,
		text: `${item.count} alertes`,
		color:
			item.status === 'NEW' ? '#FACC15' :        // text-yellow-600 → #FACC15
				item.status === 'ASSIGNED' ? '#9333EA' :   // text-purple-600 → #9333EA
					item.status === 'UNDER_REVIEW' ? '#2563EB' : // text-blue-600 → #2563EB
						item.status === 'RESOLVED' ? '#16A34A' :   // text-green-600 → #16A34A
							item.status === 'CLOSED' ? '#4B5563' :     // text-gray-600 → #4B5563
								'#2563EB'                                 // default → blue
	}));


	const columnChartData = alertsData?.alertsByMonth;

	const categoryChartData = alertsData?.alertsByCategory?.map(item => ({
		x: item.category,
		y: item.count,
		color: item.severity === 'HIGH' ? '#FF6B6B' :
			item.severity === 'MODERATE' ? '#FFE66D' : '#4ECDC4'
	}));

	const StatCard = ({ title, value, icon, color, bgColor }) => (
		<div className={`${bgColor} p-6 rounded-lg shadow-lg`}>
			<div className="flex items-center justify-between">
				<div>
					<p className="text-gray-600 text-sm font-medium">{title}</p>
					<p className={`text-2xl font-bold ${color}`}>{value}</p>
				</div>
				<div className={`${color} text-3xl`}>
					{icon}
				</div>
			</div>
		</div>
	);

	return (
		<div className='md:m-10 mt-auto p-2 pb-4 md:p-10 bg-gray-200 md:rounded-3xl rounded-xl w-100'>
			<Header category={`Tableau de bord`} title='' />
			<div className="space-y-6">
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
					<StatCard
						title="Total Alertes"
						value={alertsData.totalAlerts}
						icon={<AiOutlineAlert />}
						color="text-blue-600"
						bgColor="bg-blue-50"
					/>
					<StatCard
						title="Nouvelles"
						value={alertsData.newAlerts}
						icon={<AiOutlineArrowDown />}
						color="text-yellow-600"
						bgColor="bg-yellow-50"
					/>
					<StatCard
						title="Assignées"
						value={alertsData.assignedAlerts}
						icon={<AiOutlineUnlock />}
						color="text-purple-600"
						bgColor="bg-purple-50"
					/>
					<StatCard
						title="En Traitement"
						value={alertsData.underReviewAlerts}
						icon={<AiOutlineEye />}
						color="text-blue-600"
						bgColor="bg-blue-50"
					/>
					<StatCard
						title="Résolues"
						value={alertsData.resolvedAlerts}
						icon={<AiOutlineCheckCircle />}
						color="text-green-600"
						bgColor="bg-green-50"
					/>
					<StatCard
						title="Cloturées"
						value={alertsData.closedAlerts}
						icon={<AiOutlineLock />}
						color="text-gray-600"
						bgColor="bg-gray-50"
					/>
				</div>

				<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
					<div className="bg-white dark:bg-secondary-dark-bg p-6 rounded-lg shadow-lg">
						<h3 className="text-lg font-semibold mb-4 dark:text-white">Répartition par statut</h3>
						<AccumulationChartComponent
							id="pie-chart"
							height="300px"
							tooltip={{ enable: true }}
							legendSettings={{ visible: true, position: 'Bottom' }}
							background={currentMode === 'Dark' ? '#33373E' : '#fff'}
						>
							<Inject services={[PieSeries, AccumulationDataLabel, AccumulationLegend, AccumulationTooltip]} />
							<AccumulationSeriesCollectionDirective>
								<AccumulationSeriesDirective
									dataSource={pieChartData}
									xName="x"
									yName="y"
									type="Pie"
									dataLabel={{ visible: true, name: 'text' }}
									pointColorMapping='color'
								/>
							</AccumulationSeriesCollectionDirective>
						</AccumulationChartComponent>
					</div>

					<div className="bg-white dark:bg-secondary-dark-bg p-6 rounded-lg shadow-lg">
						<h3 className="text-lg font-semibold mb-4 dark:text-white">Évolution mensuelle</h3>
						<ChartComponent
							id="column-chart"
							height="300px"
							primaryXAxis={primaryXAxis}
							primaryYAxis={primaryYAxis}
							tooltip={{ enable: true }}
							background={currentMode === 'Dark' ? '#33373E' : '#fff'}
						>
							<Inject services={[ColumnSeries, DataLabel, Category, Tooltip, Legend]} />
							<SeriesCollectionDirective>
								<SeriesDirective
									dataSource={columnChartData}
									xName="month"
									yName="count"
									type="Column"
									fill={currentColor}
									dataLabel={{ visible: true }}
								/>
							</SeriesCollectionDirective>
						</ChartComponent>
					</div>
				</div>

				<div className="bg-white dark:bg-secondary-dark-bg p-6 rounded-lg shadow-lg">
					<h3 className="text-lg font-semibold mb-4 dark:text-white">Alertes par catégorie</h3>
					<ChartComponent
						id="category-chart"
						height="300px"
						primaryXAxis={{ valueType: 'Category', title: 'Catégories' }}
						primaryYAxis={{ title: 'Nombre d\'alertes' }}
						tooltip={{ enable: true }}
						background={currentMode === 'Dark' ? '#33373E' : '#fff'}
					>
						<Inject services={[ColumnSeries, DataLabel, Category, Tooltip, Legend]} />
						<SeriesCollectionDirective>
							<SeriesDirective
								dataSource={categoryChartData}
								xName="x"
								yName="y"
								type="Column"
								pointColorMapping="color"
								dataLabel={{ visible: true }}
							/>
						</SeriesCollectionDirective>
					</ChartComponent>
				</div>

				<div className="bg-white dark:bg-secondary-dark-bg p-6 rounded-lg shadow-lg">
					<h3 className="text-lg font-semibold mb-4 dark:text-white">Alertes récentes</h3>
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-gray-200">
							<thead className="bg-gray-50 dark:bg-gray-700">
								<tr>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
										Description
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
										Catégorie
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
										Statut
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
										Lieu
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
										Date
									</th>
								</tr>
							</thead>
							<tbody className="bg-white dark:bg-secondary-dark-bg divide-y divide-gray-200">
								{alertsData.recentAlerts.map((alert) => (
									<tr key={alert._id}>
										<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
											{truncate(alert.description, 50)}
										</td>
										<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
											{getCategory(alert.categoryId)?.title}
										</td>
										<td className="px-6 py-4 whitespace-nowrap">
											{
												getStatusBadge(alert.status)
											}
										</td>
										<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
											{alert.location.displayName}
										</td>
										<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
											{formatDate(alert.createdAt)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>

				<LoadingOverlay loading={loading} />
			</div>

		</div>
	);
};

export default Dashboard;